// Product spotlight

class ProductSpotlight extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.viewport = this.querySelector('.product-spotlight__viewport');
    this.slides = Array.from(this.track.children);
    this.count = parseInt(this.dataset.count, 10) || 0;
    this.loop = this.hasAttribute('loop');
    this.position = parseInt(this.dataset.position, 10) || 0;
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.prevButton = this.querySelector('[data-prev]');
    this.nextButton = this.querySelector('[data-next]');
    this.toggleButton = this.querySelector('[data-autoplay-toggle]');
    this.live = this.querySelector('[data-live]');

    if (this.count < 2) return;

    this.querySelectorAll('[data-clone] a, [data-clone] button').forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });

    this.abortController = new AbortController();
    const options = { signal: this.abortController.signal };

    this.prevButton?.addEventListener('click', () => this.step(-1), options);
    this.nextButton?.addEventListener('click', () => this.step(1), options);
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.goToSlide(parseInt(dot.dataset.dot, 10)), options);
    });

    this.track.addEventListener('transitionend', this.onTransitionEnd.bind(this), options);
    this.viewport.addEventListener('click', this.onClick.bind(this), { ...options, capture: true });
    this.viewport.addEventListener('pointerdown', this.onPointerDown.bind(this), options);
    this.viewport.addEventListener('pointerup', this.onPointerUp.bind(this), options);
    this.viewport.addEventListener('pointercancel', () => (this.pointer = null), options);
    this.viewport.addEventListener('dragstart', (event) => event.preventDefault(), options);
    this.addEventListener('keydown', this.onKeydown.bind(this), options);
    this.track.addEventListener('focusin', this.onFocusIn.bind(this), options);

    this.addEventListener('shopify:block:select', (event) => {
      const position = this.slides.indexOf(event.target);
      if (position > -1) this.goToSlide(this.realIndex(position));
    }, options);

    this.setupAutoplay(options);

    if (this.hasAttribute('data-stack-mobile')) {
      this.stackedQuery = window.matchMedia('(max-width: 767px)');
      this.stackedQuery.addEventListener('change', () => this.updateStacked(), options);
      this.updateStacked();
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.observer?.disconnect();
    this.stopAutoplay();
  }

  updateStacked() {
    this.stacked = this.stackedQuery.matches;

    this.slides.forEach((slide, index) => {
      const isClone = slide.hasAttribute('data-clone');
      slide.classList.toggle('is-active', (this.stacked && !isClone) || (!this.stacked && index === this.position));
    });

    this.setPaused?.('stacked', this.stacked);
  }

  get lastPosition() {
    return this.slides.length - 1;
  }

  realIndex(position = this.position) {
    return ((position % this.count) + this.count) % this.count;
  }

  step(delta, options = {}) {
    if (!this.loop) {
      this.moveTo(Math.min(Math.max(this.position + delta, 0), this.lastPosition), options);
      return;
    }

    const target = this.position + delta;
    if (target < 0 || target > this.lastPosition) this.jumpToMiddle();

    this.moveTo(this.position + delta, options);
  }

  goToSlide(index, options = {}) {
    if (!this.loop) {
      this.moveTo(index, options);
      return;
    }

    const copies = [index, index + this.count, index + this.count * 2];
    const nearest = copies.reduce((best, copy) => (
      Math.abs(copy - this.position) < Math.abs(best - this.position) ? copy : best
    ));

    this.moveTo(nearest, options);
  }

  moveTo(position, { immediate = false, announce = !immediate } = {}) {
    if (this.stacked || (position === this.position && !immediate)) return;

    this.position = position;

    if (immediate) this.classList.add('is-jumping');

    this.style.setProperty('--ps-index', this.position);
    this.slides.forEach((slide, index) => slide.classList.toggle('is-active', index === this.position));
    this.updateControls();

    if (immediate) {
      void this.offsetWidth;
      this.classList.remove('is-jumping');
    }

    if (announce) this.announce();
  }

  jumpToMiddle() {
    this.moveTo(this.count + this.realIndex(), { immediate: true });
  }

  announce() {
    if (!this.live) return;

    const title = this.slides[this.position].querySelector('.product-spotlight__title')?.textContent.trim();
    const label = `${this.realIndex() + 1} of ${this.count}`;

    this.live.textContent = title ? `${label}: ${title}` : label;
  }

  updateControls() {
    const index = this.realIndex();

    this.dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === index;
      dot.classList.toggle('is-active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });

    if (!this.loop) {
      if (this.prevButton) this.prevButton.disabled = this.position === 0;
      if (this.nextButton) this.nextButton.disabled = this.position === this.lastPosition;
    }
  }

  onTransitionEnd(event) {
    if (event.target !== this.track || event.propertyName !== 'transform') return;

    if (this.loop && (this.position < this.count || this.position >= this.count * 2)) {
      this.jumpToMiddle();
    }
  }

  onClick(event) {
    if (this.stacked) return;

    if (this.swiped) {
      this.swiped = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const slide = event.target.closest('.product-spotlight__slide');
    if (!slide || slide.classList.contains('is-active') || event.target.closest('a')) return;

    this.moveTo(this.slides.indexOf(slide));
  }

  onFocusIn(event) {
    this.viewport.scrollLeft = 0;

    const slide = event.target.closest('.product-spotlight__slide');
    if (!slide || slide.classList.contains('is-active') || !event.target.matches(':focus-visible')) return;

    this.moveTo(this.slides.indexOf(slide), { announce: false });
  }

  onPointerDown(event) {
    this.swiped = false;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.pointer = { x: event.clientX, y: event.clientY };
  }

  onPointerUp(event) {
    if (!this.pointer || this.stacked) return;

    const deltaX = event.clientX - this.pointer.x;
    const deltaY = event.clientY - this.pointer.y;
    this.pointer = null;

    if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return;

    this.swiped = true;
    this.step(deltaX < 0 ? 1 : -1);
  }

  onKeydown(event) {
    if (this.stacked || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;

    event.preventDefault();
    this.step(event.key === 'ArrowRight' ? 1 : -1);
  }

  setupAutoplay(options) {
    const seconds = parseFloat(this.getAttribute('autoplay'));
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!seconds || window.Shopify?.designMode || reducedMotion) {
      if (this.toggleButton) this.toggleButton.hidden = true;
      return;
    }

    this.autoplayDelay = seconds * 1000;
    this.pauseReasons = new Set();

    const setPaused = this.setPaused = (reason, paused) => {
      if (paused) {
        this.pauseReasons.add(reason);
      } else {
        this.pauseReasons.delete(reason);
      }

      if (this.pauseReasons.size) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    };

    this.addEventListener('pointerenter', () => setPaused('hover', true), options);
    this.addEventListener('pointerleave', () => setPaused('hover', false), options);
    this.addEventListener('focusin', () => setPaused('focus', true), options);
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) setPaused('focus', false);
    }, options);

    this.observer = new IntersectionObserver(([entry]) => setPaused('offscreen', !entry.isIntersecting));
    this.observer.observe(this);

    this.toggleButton?.addEventListener('click', () => {
      const pausing = !this.pauseReasons.has('user');

      if (!pausing) {
        this.pauseReasons.delete('hover');
        this.pauseReasons.delete('focus');
      }

      setPaused('user', pausing);
      this.classList.toggle('is-paused', pausing);
      this.toggleButton.setAttribute('aria-label', this.toggleButton.dataset[pausing ? 'playLabel' : 'pauseLabel']);
    }, options);
  }

  startAutoplay() {
    this.stopAutoplay();

    this.autoplayTimer = setInterval(() => {
      if (!this.loop && this.position === this.lastPosition) {
        this.moveTo(0, { announce: false });
      } else {
        this.step(1, { announce: false });
      }
    }, this.autoplayDelay);
  }

  stopAutoplay() {
    clearInterval(this.autoplayTimer);
  }
}

if (!customElements.get('product-spotlight')) {
  customElements.define('product-spotlight', ProductSpotlight);
}

// Ingredients

class IngredientsGrid extends HTMLElement {
  connectedCallback() {
    this.drawer = document.getElementById(this.dataset.drawer);
    this.status = this.querySelector('[data-ingredients-status]');
    this.abortController = new AbortController();

    this.addEventListener('click', (event) => {
      const card = event.target.closest('[data-ingredient]');
      if (card) this.fillDrawer(card);

      const filter = event.target.closest('[data-filter]');
      if (filter) this.toggleFilter(filter);
    }, { capture: true, signal: this.abortController.signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  fillDrawer(card) {
    const template = this.querySelector(`[data-ingredient-template="${card.dataset.ingredient}"]`);
    const content = this.drawer?.querySelector('[data-ingredient-content]');
    if (!template || !content) return;

    content.replaceChildren(template.content.cloneNode(true));
    content.scrollTop = 0;
    content.closest('.drawer__content')?.scrollTo(0, 0);

    const title = this.drawer.querySelector('[data-ingredient-title]');
    if (title) title.textContent = card.dataset.name;
  }

  toggleFilter(button) {
    const pressed = button.getAttribute('aria-pressed') !== 'true';
    const value = pressed ? button.dataset.filter : null;

    this.querySelectorAll('[data-filter]').forEach((filter) => {
      filter.setAttribute('aria-pressed', String(filter === button && pressed));
    });

    let visible = 0;
    this.querySelectorAll('[data-filters]').forEach((item) => {
      const show = !value || item.dataset.filters.split(' ').includes(value);
      item.hidden = !show;
      if (show) visible += 1;
    });

    if (this.status) this.status.textContent = `${visible} ingredients`;
  }
}

if (!customElements.get('ingredients-grid')) {
  customElements.define('ingredients-grid', IngredientsGrid);
}

// Scroll story

class ScrollStory extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-item]'));
    this.images = Array.from(this.querySelectorAll('[data-media]'));
    this.media = this.querySelector('.scroll-story__media');
    this.index = 0;

    if (this.items.length < 2) return;

    this.onScroll = theme.utils.throttle(this.update.bind(this));

    this.update();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    this.onScroll?.cancel?.();
  }

  get line() {
    const rect = this.media?.getBoundingClientRect();
    if (!rect || rect.height === 0) return window.innerHeight * 0.5;

    const percent = parseFloat(this.dataset.activation);
    return rect.top + rect.height * ((isNaN(percent) ? 90 : percent) / 100);
  }

  update() {
    const line = this.line;
    let active = 0;

    this.items.forEach((item, index) => {
      if (item.getBoundingClientRect().top <= line + 1) active = index;
    });

    this.setActive(active);
  }

  setActive(index) {
    if (index < 0 || index === this.index) return;

    this.index = index;
    this.items.forEach((item, i) => item.classList.toggle('is-active', i === index));
    this.images.forEach((image, i) => image.classList.toggle('is-active', i === index));
  }
}

if (!customElements.get('scroll-story')) {
  customElements.define('scroll-story', ScrollStory);
}

// Feature reveal

class FeatureReveal extends HTMLElement {
  connectedCallback() {
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.observer.disconnect();
        this.classList.add('is-revealed');
      },
      { rootMargin: '0px 0px -15% 0px' }
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

if (!customElements.get('feature-reveal')) {
  customElements.define('feature-reveal', FeatureReveal);
}

// Spotlight video

class SpotlightVideo extends HTMLElement {
  static prefersHevc() {
    const ua = navigator.userAgent;
    const iOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const safari = /Safari\//.test(ua) && !/(Chrome|Chromium|CriOS|Edg|OPR|Firefox|FxiOS)\//.test(ua) && !/Android/.test(ua);
    return iOS || safari;
  }

  connectedCallback() {
    this.video = this.querySelector('video[data-alpha]');
    this.media = this.video ? null : this.querySelector('video-media');
    this.card = this.closest('.product-spotlight__card');
    if ((!this.video && !this.media) || !this.card) return;

    this.abortController = new AbortController();
    const options = { signal: this.abortController.signal };

    if (this.video) {
      this.video.muted = true;
      this.video.src = SpotlightVideo.prefersHevc() ? this.dataset.hevc : this.dataset.webm;
      this.video.addEventListener('playing', () => this.classList.add('is-ready'), { ...options, once: true });
    }

    this.hoverQuery = window.matchMedia('(hover: hover) and (min-width: 768px)');
    this.hoverQuery.addEventListener('change', () => this.setMode(), options);

    this.card.addEventListener('pointerenter', () => this.hoverQuery.matches && this.play(), options);
    this.card.addEventListener('pointerleave', () => this.hoverQuery.matches && this.pause(), options);
    this.card.addEventListener('focusin', () => this.hoverQuery.matches && this.play(), options);
    this.card.addEventListener('focusout', (event) => {
      if (this.hoverQuery.matches && !this.card.contains(event.relatedTarget)) this.pause();
    }, options);
    this.card.addEventListener('click', (event) => {
      if (this.hoverQuery.matches || event.target.closest('a, button')) return;
      this.isPaused() ? this.play() : this.pause();
    }, options);

    this.setMode();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.gestureController?.abort();
    this.observer?.disconnect();
  }

  setMode() {
    this.observer?.disconnect();
    this.pause();

    if (this.hoverQuery.matches || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.observer = new IntersectionObserver(([entry]) => {
      entry.isIntersecting ? this.play() : this.pause();
    }, { threshold: 0.5 });
    this.observer.observe(this);
  }

  play() {
    if (this.video) {
      this.video.play().catch((error) => {
        if (error.name === 'NotAllowedError') this.retryOnGesture();
      });
      return;
    }

    this.media.paused = false;
    this.media.play();
  }

  pause() {
    (this.video || this.media).pause();
  }

  isPaused() {
    return this.video ? this.video.paused : this.media.paused !== false;
  }

  retryOnGesture() {
    if (this.gestureController) return;
    this.gestureController = new AbortController();

    const retry = () => {
      const rect = this.getBoundingClientRect();
      if (!(rect.width > 0 && rect.bottom > 0 && rect.top < window.innerHeight)) return;

      this.gestureController.abort();
      this.gestureController = null;
      this.video.muted = true;
      this.video.play().catch(() => {});
    };

    ['pointerup', 'touchend', 'click'].forEach((type) => {
      document.addEventListener(type, retry, { signal: this.gestureController.signal, passive: true });
    });
  }
}

if (!customElements.get('spotlight-video')) {
  customElements.define('spotlight-video', SpotlightVideo);
}

// Cart recommendations: equal card heights

const equalizeRecommendations = (recommendations) => {
  const cards = recommendations.querySelectorAll('.horizontal-product');
  if (cards.length < 2) return;

  cards.forEach((card) => (card.style.minHeight = ''));
  const tallest = Math.max(...Array.from(cards, (card) => card.offsetHeight));
  cards.forEach((card) => (card.style.minHeight = `${tallest}px`));

  recommendations.querySelector('product-complementary')?.carousel?.slider?.resize();
};

document.addEventListener('recommendations:loaded', (event) => {
  if (event.target.closest('.cart-drawer')) equalizeRecommendations(event.target);
}, true);

window.addEventListener('resize', theme.utils.throttle(() => {
  document.querySelectorAll('.cart-drawer product-recommendations').forEach(equalizeRecommendations);
}), { passive: true });

// Collage captions

class CollageCaptions extends HTMLElement {
  connectedCallback() {
    this.observer = new ResizeObserver(() => this.measure());
    this.querySelectorAll('.carousel-collage__below').forEach((caption) => this.observer.observe(caption));
    this.measure();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  measure() {
    const captions = this.querySelectorAll('.carousel-collage__below');
    if (!captions.length) return;

    const tallest = Math.max(...Array.from(captions, (caption) => caption.offsetHeight));
    const gap = captions[0].offsetTop - captions[0].parentElement.clientHeight;
    this.style.setProperty('--cc-caption-space', `${Math.ceil(tallest + gap)}px`);
  }
}

if (!customElements.get('collage-captions')) {
  customElements.define('collage-captions', CollageCaptions);
}

// Scroll collage

class ScrollCollage extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.tiles = Array.from(this.track?.children || []);
    if (!this.tiles.length) return;

    this.desktopQuery = window.matchMedia('(min-width: 768px)');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.distance = (parseFloat(this.dataset.distance) || 35) / 100;
    this.played = this.reducedMotion;
    this.placed = this.reducedMotion;

    this.onResize = () => this.place();
    window.addEventListener('resize', this.onResize, { passive: true });
    window.addEventListener('load', this.onResize, { once: true });
    this.desktopQuery.addEventListener('change', this.onResize);

    this.observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      this.observer.disconnect();
      this.played = true;
      this.classList.add('is-playing');
      this.place();
      if (!this.desktopQuery.matches && !this.reducedMotion) this.autoScroll();
    }, { threshold: 0.25 });
    this.observer.observe(this);

    this.place();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.stopScroll?.();
    window.removeEventListener('resize', this.onResize);
    this.desktopQuery?.removeEventListener('change', this.onResize);
  }

  autoScroll() {
    const start = this.scrollLeft;
    const distance = this.scrollWidth - this.clientWidth - start;
    if (distance <= 0) return;

    const duration = (parseFloat(getComputedStyle(this).getPropertyValue('--sc-duration')) || 2.2) * 1000;
    const events = ['pointerdown', 'touchstart', 'wheel'];
    let startTime = null;
    let frame;

    this.stopScroll = () => {
      cancelAnimationFrame(frame);
      this.style.scrollSnapType = '';
      events.forEach((type) => this.removeEventListener(type, this.stopScroll));
      this.stopScroll = null;
    };
    events.forEach((type) => this.addEventListener(type, this.stopScroll, { passive: true }));
    this.style.scrollSnapType = 'none';

    const step = (now) => {
      startTime ??= now;
      const progress = Math.min((now - startTime) / duration, 1);
      this.scrollLeft = start + distance * (1 - Math.pow(1 - progress, 5));
      if (progress < 1) frame = requestAnimationFrame(step);
      else this.stopScroll?.();
    };
    frame = requestAnimationFrame(step);
  }

  get alignTarget() {
    if (!this.hasAttribute('data-align-scroll-story')) return null;

    let section = this.closest('.shopify-section')?.nextElementSibling;
    while (section) {
      const target = section.querySelector('.scroll-story__item > .grid');
      if (target) return target;
      section = section.nextElementSibling;
    }
    return null;
  }

  place() {
    const animate = this.classList.contains('is-playing') && !this.placed;
    this.track.style.transition = 'none';
    this.track.style.transform = '';

    if (!this.desktopQuery.matches) return;

    const last = this.tiles[this.tiles.length - 1].getBoundingClientRect();
    const target = this.alignTarget;
    const stop = target ? target.getBoundingClientRect().left : this.getBoundingClientRect().right - last.width;
    const endX = stop - last.left;
    const startX = endX + window.innerWidth * this.distance;

    if (!this.played) {
      this.track.style.transform = `translate3d(${startX}px, 0, 0)`;
      return;
    }

    if (animate) {
      this.track.style.transform = `translate3d(${startX}px, 0, 0)`;
      void this.track.offsetWidth;
      this.track.style.transition = '';
      this.placed = true;
    }

    this.track.style.transform = `translate3d(${endX}px, 0, 0)`;
  }
}

if (!customElements.get('scroll-collage')) {
  customElements.define('scroll-collage', ScrollCollage);
}

// Scroll gallery

class ScrollGallery extends HTMLElement {
  connectedCallback() {
    this.images = Array.from(this.querySelectorAll('[data-gallery-image]'));
    this.texts = Array.from(this.querySelectorAll('[data-gallery-text]'));
    this.sticky = this.querySelector('.scroll-gallery__sticky');
    this.index = 0;
    if (this.texts.length < 2) return;

    this.onScroll = theme.utils.throttle(this.update.bind(this));
    this.resizeObserver = new ResizeObserver(() => {
      this.sticky.style.setProperty('--sg-text-height', `${this.sticky.offsetHeight}px`);
      this.onScroll();
    });
    this.resizeObserver.observe(this.sticky);
    this.update();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });

    this.texts.forEach((text, index) => {
      text.addEventListener('shopify:block:select', () => this.setActive(index));
    });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    this.resizeObserver?.disconnect();
    this.onScroll?.cancel?.();
  }

  update() {
    const rect = this.sticky.getBoundingClientRect();
    const line = rect.top + rect.height / 2;
    let active = 0;

    this.images.forEach((image, index) => {
      if (image.getBoundingClientRect().top <= line) active = index;
    });

    this.setActive(active);
  }

  setActive(index) {
    if (index === this.index && this.texts[index].classList.contains('is-active')) return;

    this.index = index;
    this.texts.forEach((text, i) => text.classList.toggle('is-active', i === index));
  }
}

if (!customElements.get('scroll-gallery')) {
  customElements.define('scroll-gallery', ScrollGallery);
}

// Scroll timeline

class ScrollTimeline extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-item]'));
    this.index = -1;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.classList.add('is-revealed');
          this.observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -15% 0px' }
    );
    this.items.forEach((item) => this.observer.observe(item));

    this.onScroll = theme.utils.throttle(this.update.bind(this));
    this.update();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    this.onScroll?.cancel?.();
  }

  update() {
    const line = window.innerHeight * 0.5;
    let active = 0;

    this.items.forEach((item, index) => {
      if (item.getBoundingClientRect().top <= line) active = index;
    });

    if (active === this.index) return;

    this.index = active;
    this.items.forEach((item, index) => item.classList.toggle('is-active', index === active));
  }
}

if (!customElements.get('scroll-timeline')) {
  customElements.define('scroll-timeline', ScrollTimeline);
}

// Vertical carousel

class VerticalCarousel extends HTMLElement {
  connectedCallback() {
    this.observer = new IntersectionObserver(
      ([entry]) => this.classList.toggle('paused', !entry.isIntersecting),
      { rootMargin: '100px 0px' }
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

if (!customElements.get('vertical-carousel')) {
  customElements.define('vertical-carousel', VerticalCarousel);
}

// Product bundle hero — gallery

if (!customElements.get('bundle-gallery')) {
  customElements.define(
    'bundle-gallery',
    class BundleGallery extends BaseElement {
      connectedCallback() {
        super.connectedCallback();

        this.track = this.querySelector('[data-track]');
        this.slides = Array.from(this.querySelectorAll('[data-slide]'));
        this.numbers = Array.from(this.querySelectorAll('[data-goto]'));
        this.prevButton = this.querySelector('[data-prev]');
        this.nextButton = this.querySelector('[data-next]');
        this.index = 0;

        if (!this.track || this.slides.length < 2) return;

        this.on(this.prevButton, 'click', () => this.step(-1));
        this.on(this.nextButton, 'click', () => this.step(1));
        this.numbers.forEach((button, index) => {
          this.on(button, 'click', () => this.goTo(index));
          this.on(button, 'keydown', (event) => this.onKeydown(event, index));
        });

        // The track is a scroll-snap container, so swiping is what moves it and
        // the scroll position is the source of truth for the active slide.
        this.on(this.track, 'scroll', theme.utils.throttle(() => this.syncFromScroll()), { passive: true });
      }

      step(direction) {
        this.goTo(Math.max(0, Math.min(this.slides.length - 1, this.index + direction)));
      }

      goTo(index, focus = false) {
        const slide = this.slides[index];
        if (!slide) return;

        const left = this.track.scrollLeft + slide.getBoundingClientRect().right - this.track.getBoundingClientRect().right;
        this.track.scrollTo({ left, behavior: theme.config.motionReduced ? 'auto' : 'smooth' });
        this.setActive(index);
        if (focus) this.numbers[index]?.focus();
      }

      syncFromScroll() {
        const edge = this.track.getBoundingClientRect().right;
        let closest = 0;
        let distance = Infinity;

        this.slides.forEach((slide, index) => {
          const delta = Math.abs(slide.getBoundingClientRect().right - edge);
          if (delta < distance) {
            distance = delta;
            closest = index;
          }
        });

        this.setActive(closest);
      }

      setActive(index) {
        if (index === this.index) return;
        this.index = index;

        this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
        this.numbers.forEach((button, i) => {
          button.classList.toggle('is-active', i === index);
          if (i === index) button.setAttribute('aria-current', 'true');
          else button.removeAttribute('aria-current');
          button.tabIndex = i === index ? 0 : -1;
        });

        if (this.prevButton) this.prevButton.disabled = index === 0;
        if (this.nextButton) this.nextButton.disabled = index === this.slides.length - 1;
      }

      onKeydown(event, index) {
        const last = this.slides.length - 1;
        let next = null;

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = index === last ? 0 : index + 1;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = index === 0 ? last : index - 1;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = last;

        if (next === null) return;
        event.preventDefault();
        this.goTo(next, true);
      }
    }
  );
}

// Product bundle hero — bundle builder
//
// Owns its own state; nothing reaches the cart until Add to cart runs, which posts
// every selected flavor to /cart/add.js in one request and then refreshes the cart
// through the theme's own pubsub channel.

if (!customElements.get('bundle-hero')) {
  customElements.define(
    'bundle-hero',
    class BundleHero extends BaseElement {
      connectedCallback() {
        super.connectedCallback();

        const configElement = this.querySelector('[data-bundle-config]');
        if (!configElement) return;

        try {
          this.config = JSON.parse(configElement.textContent);
        } catch (error) {
          console.warn('[bundle-hero] could not parse section config', error);
          return;
        }

        this.flavors = (this.config.flavors || []).filter((flavor) => flavor && flavor.variant);
        if (!this.flavors.length) return;

        // Sold-out flavors still render (greyed out by Liquid) but must never be
        // auto-filled or posted to /cart/add.js — that would 422 the whole request.
        this.sellable = this.flavors.filter((flavor) => flavor.available !== false);
        if (!this.sellable.length) return;

        this.pouches = this.number(this.config.pouchesPerPack, 6);
        this.maxPerFlavor = this.number(this.config.maxPerFlavor, 6);
        this.anchor = this.number(this.config.anchor, 39.99);

        this.plans = (this.config.plans || []).map((plan) => ({
          key: plan.key,
          name: plan.name || '',
          kind: plan.kind === 'one_time' ? 'one_time' : 'subscription',
          termMonths: Math.max(1, this.number(plan.termMonths, 1)),
          tiers: this.parseTiers(plan.tiers),
          recurring: plan.recurring != null && String(plan.recurring).trim() !== '' ? this.number(plan.recurring, 0) : null,
          isDefault: plan.isDefault === true,
        }));
        if (!this.plans.length) return;

        const defaultPlan = this.plans.find((plan) => plan.isDefault) || this.plans[0];
        this.state = { type: defaultPlan.key, qty: {} };
        this.flavors.forEach((flavor) => (this.state.qty[flavor.key] = 0));

        this.previousActive = 0;
        this.cta = this.q('[data-cta]');
        this.liveRegion = this.q('[data-live]');
        this.registerCleanup(() => clearTimeout(this.liveTimer));

        this.wireRadios('[data-plans]', (element) => {
          this.state.type = element.dataset.type;
          this.recalc();
        });
        this.wireRadios('[data-ladder]', (element) => {
          this.autofill(parseInt(element.dataset.packs, 10));
          this.recalc();
        });
        this.wireSteppers();

        if (this.cta) this.on(this.cta, 'click', this.onSubmit.bind(this));

        this.autofill(this.number(this.config.defaultPacks, 0));

        // Reflect the configured default plan in the radio group.
        this.qa('[data-plans] [role="radio"]').forEach((element) => {
          const on = element.dataset.type === this.state.type;
          element.setAttribute('aria-checked', on ? 'true' : 'false');
          element.tabIndex = on ? 0 : -1;
        });

        this.recalc();
        this.startCountdown();
      }

      // ---------- helpers ----------

      q(selector) {
        return this.querySelector(selector);
      }

      qa(selector) {
        return Array.from(this.querySelectorAll(selector));
      }

      number(value, fallback) {
        const parsed = parseFloat(value);
        return isFinite(parsed) ? parsed : fallback;
      }

      round2(value) {
        return Math.round(value * 100) / 100;
      }

      money(value) {
        const format = theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat;
        return theme.Currency.formatMoney(Math.round((Number(value) || 0) * 100), format);
      }

      // Expand {placeholder} tokens in a merchant-editable string.
      fill(template, values) {
        return Object.keys(values).reduce((out, key) => out.split(`{${key}}`).join(values[key]), String(template || ''));
      }

      packLabel(count) {
        const template = count === 1 ? this.config.packLabel : this.config.packLabelPlural;
        return this.fill(template || '{n} packs', { n: count });
      }

      pouchLabel(count) {
        return this.fill(this.config.pouchLabel || '{n} pouches', { n: count });
      }

      // "29.99, 24.49, 23.49" -> [29.99, 24.49, 23.49]. Index 0 is 1 pack; the last
      // value applies to every higher pack count.
      parseTiers(value) {
        const tiers = String(value || '')
          .split(',')
          .map((entry) => parseFloat(entry))
          .filter((entry) => isFinite(entry));
        return tiers.length ? tiers : [0];
      }

      // ---------- pricing ----------

      currentPlan() {
        return this.plans.find((plan) => plan.key === this.state.type) || this.plans[0];
      }

      isSubscription() {
        return this.currentPlan().kind === 'subscription';
      }

      // Per-pack intro rate for a plan at n packs. The last tier holds for 4+.
      planPerPack(plan, count) {
        const n = count < 1 ? 1 : count;
        return plan.tiers[Math.min(n, plan.tiers.length) - 1];
      }

      retail(count) {
        return this.round2(this.anchor * count);
      }

      // Charge today. Deliveries are always monthly, so a subscription billed on a
      // multi-month term pays that many deliveries up front — Quarterly (term 3)
      // charges 3 monthly boxes at once. Monthly and one-time stay at x1.
      charge(count) {
        const plan = this.currentPlan();
        const multiplier = plan.kind === 'subscription' ? plan.termMonths : 1;
        return this.round2(this.planPerPack(plan, count) * count * multiplier);
      }

      // Every order ships free, so the charge today is the product total and savings
      // are a straight product-price comparison.
      saveAmount(count) {
        return this.round2(this.retail(count) - this.round2(this.planPerPack(this.currentPlan(), count) * count));
      }

      savePercent(count) {
        const retail = this.retail(count);
        return retail > 0 ? Math.round((this.saveAmount(count) / retail) * 100) : 0;
      }

      savePercentFor(plan, count) {
        const retail = this.retail(count);
        const paid = this.round2(this.planPerPack(plan, count) * count);
        return retail > 0 ? Math.round(((retail - paid) / retail) * 100) : 0;
      }

      totalPacks() {
        return this.flavors.reduce((total, flavor) => total + this.state.qty[flavor.key], 0);
      }

      // Spread n packs across the in-stock flavors, round-robin.
      autofill(count) {
        this.flavors.forEach((flavor) => (this.state.qty[flavor.key] = 0));
        for (let i = 0; i < count; i++) {
          const flavor = this.sellable[i % this.sellable.length];
          if (this.state.qty[flavor.key] < this.maxPerFlavor) this.state.qty[flavor.key]++;
        }
      }

      // ---------- renderers ----------

      renderFlavors() {
        this.qa('[data-flavors] .bundle-hero__flavor').forEach((row) => {
          const quantity = this.state.qty[row.dataset.flavor] || 0;

          const value = row.querySelector('[data-qty]');
          if (value) value.textContent = quantity;

          row.classList.toggle('is-selected', quantity > 0);

          const packline = row.querySelector('[data-packline]');
          if (packline) {
            const packs = Math.max(quantity, 1);
            packline.textContent = `${this.packLabel(packs)} · ${this.pouchLabel(packs * this.pouches)}`;
          }

          const minus = row.querySelector('[data-step="-1"]');
          if (minus) minus.disabled = quantity <= 0;

          const plus = row.querySelector('[data-step="1"]');
          if (plus) plus.disabled = quantity >= this.maxPerFlavor;
        });
      }

      renderLadder() {
        const total = this.totalPacks();
        const active = total < 1 ? 0 : Math.min(total, 4);

        const ladder = this.q('[data-ladder]');
        if (ladder) ladder.style.setProperty('--bundle-progress', active <= 1 ? 0 : (active - 1) / 3);

        this.qa('[data-ladder] .bundle-hero__tier').forEach((tier, index) => {
          const packs = parseInt(tier.dataset.packs, 10);
          const isCurrent = packs === active;
          const reached = active > 0 && packs <= active;

          tier.setAttribute('aria-checked', isCurrent ? 'true' : 'false');
          tier.tabIndex = isCurrent || (active === 0 && index === 0) ? 0 : -1;
          tier.classList.toggle('is-reached', reached);
          tier.classList.toggle('is-current', isCurrent);

          const price = tier.querySelector('[data-tier-price]');
          if (price) price.textContent = this.fill(this.config.perPackText || '{price}/pack', { price: this.money(this.planPerPack(this.currentPlan(), packs)) });

          const save = tier.querySelector('[data-tier-save]');
          const percent = this.savePercent(packs);
          // Subscriptions show every tier; one-time hides the 1-pack tier.
          const showSave = percent > 0 && !(!this.isSubscription() && packs <= 1);
          if (save) {
            save.textContent = showSave ? this.fill(this.config.saveText || 'Save {percent}%', { percent }) : '';
            save.hidden = !showSave;
          }
        });

        if (active > this.previousActive && active > 0) {
          const tier = this.q(`[data-ladder] .bundle-hero__tier[data-packs="${active}"]`);
          if (tier) {
            tier.classList.remove('is-rippling');
            void tier.offsetWidth;
            tier.classList.add('is-rippling');
          }
          const badge = this.q(`[data-badge="${active}"]`);
          if (badge) {
            badge.classList.remove('is-popping');
            void badge.offsetWidth;
            badge.classList.add('is-popping');
          }
        }
        this.previousActive = active;
      }

      renderPlans(packs) {
        const anchor = this.money(this.anchor);

        this.plans.forEach((plan) => {
          const card = this.q(`[data-plans] [data-type="${plan.key}"]`);
          if (!card) return;

          const perPack = this.planPerPack(plan, packs);
          const isSubscription = plan.kind === 'subscription';

          const pack = card.querySelector('[data-plan-pack]');
          if (pack) {
            pack.innerHTML = isSubscription
              ? `<span class="sr-only">Original price</span> <s>${anchor}</s> <span class="sr-only">Discounted price</span> <span>${this.money(perPack)} / pack</span>`
              : `<span>${this.money(perPack)} / pack</span>`;
          }

          const after = card.querySelector('[data-plan-after]');
          if (after) {
            const show = isSubscription && plan.recurring != null;
            after.textContent = show ? this.fill(this.config.afterText || 'then {price} / pack', { price: this.money(plan.recurring) }) : '';
            after.hidden = !show;
          }

          const tag = card.querySelector('[data-plan-tag]');
          if (tag) {
            const percent = this.savePercentFor(plan, packs);
            if (percent <= 0) {
              tag.textContent = '';
            } else if (plan.termMonths > 1) {
              tag.textContent = this.fill(this.config.saveTermText || 'Save {percent}% for {months} months', { percent, months: plan.termMonths });
            } else {
              tag.textContent = this.fill(this.config.saveText || 'Save {percent}%', { percent });
            }
          }
        });
      }

      renderSavings(packs) {
        const element = this.q('[data-savings]');
        if (!element) return;

        const nodes = [];
        const span = (className, text) => {
          const node = document.createElement('span');
          node.className = className;
          node.textContent = text;
          return node;
        };

        if (packs <= 0) {
          if (this.config.savingsEmpty) nodes.push(document.createTextNode(this.config.savingsEmpty));
        } else if (this.savePercent(packs) > 0) {
          const percent = this.savePercent(packs);
          const [before, after = ''] = this.fill(this.config.savingsText || '', { percent }).split('{amount}');
          nodes.push(document.createTextNode(before));
          if (this.config.savingsText?.includes('{amount}')) nodes.push(span('bundle-hero__savings-amount', this.money(this.saveAmount(packs))));
          nodes.push(document.createTextNode(after));

          if (this.isSubscription()) {
            const plan = this.currentPlan();
            const better = this.planPerPack(plan, packs + 1) < this.planPerPack(plan, packs);
            const tail = better ? this.fill(this.config.savingsNudge || '', { next: this.savePercent(packs + 1) }) : this.config.savingsBest || '';
            if (tail.trim()) nodes.push(span('bundle-hero__savings-nudge', tail));
          }
        } else {
          nodes.push(document.createTextNode(`${this.packLabel(packs)} · ${this.pouchLabel(packs * this.pouches)}`));
        }

        element.replaceChildren(...nodes);
        element.hidden = element.textContent.trim() === '';
      }

      renderCta(packs) {
        if (!this.cta) return;

        const label = this.q('[data-cta-text]');
        const disabled = packs <= 0;

        this.cta.disabled = disabled;
        this.cta.setAttribute('aria-disabled', disabled ? 'true' : 'false');

        if (label) {
          label.textContent = disabled
            ? this.config.ctaEmptyText || 'Add flavors to start'
            : `${this.config.ctaText || 'Add to cart'} · ${this.money(this.charge(packs))}`;
        }
      }

      recalc() {
        const packs = this.totalPacks();

        this.renderFlavors();
        this.renderLadder();
        this.renderPlans(packs || 1);
        this.renderSavings(packs);
        this.renderCta(packs);
      }

      // ---------- input wiring ----------

      wireRadios(groupSelector, onSelect) {
        const group = this.q(groupSelector);
        if (!group) return;

        const items = Array.from(group.children).filter((element) => element.getAttribute('role') === 'radio');

        const select = (element) => {
          items.forEach((item) => {
            const on = item === element;
            item.setAttribute('aria-checked', on ? 'true' : 'false');
            item.tabIndex = on ? 0 : -1;
          });
          onSelect(element);
        };

        items.forEach((element, index) => {
          this.on(element, 'click', () => {
            select(element);
            element.focus();
          });
          this.on(element, 'keydown', (event) => {
            if (event.key === ' ' || event.key === 'Enter') {
              event.preventDefault();
              select(element);
            } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
              event.preventDefault();
              const next = items[(index + 1) % items.length];
              select(next);
              next.focus();
            } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
              event.preventDefault();
              const previous = items[(index - 1 + items.length) % items.length];
              select(previous);
              previous.focus();
            }
          });
        });
      }

      wireSteppers() {
        this.qa('[data-flavors] [data-add]').forEach((button) => {
          this.on(button, 'click', () => {
            const row = button.closest('.bundle-hero__flavor');
            this.state.qty[row.dataset.flavor] = 1;
            this.recalc();

            row.querySelector('[data-step="1"]')?.focus();
            const name = row.querySelector('[data-flavor-name]')?.textContent.trim() || 'flavor';
            this.announce(`${name} added, quantity 1`);
          });
        });

        this.qa('[data-flavors] [data-step]').forEach((button) => {
          this.on(button, 'click', () => {
            const row = button.closest('.bundle-hero__flavor');
            const key = row.dataset.flavor;
            const previous = this.state.qty[key];

            this.state.qty[key] = Math.max(0, Math.min(this.maxPerFlavor, previous + parseInt(button.dataset.step, 10)));
            const next = this.state.qty[key];
            if (next === previous) return;

            const value = row.querySelector('[data-qty]');
            if (value) {
              value.classList.remove('is-bumping');
              void value.offsetWidth;
              value.classList.add('is-bumping');
            }

            this.recalc();

            const name = row.querySelector('[data-flavor-name]')?.textContent.trim() || 'flavor';
            this.announce(next === 0 ? `${name} removed` : `${name} quantity ${next > previous ? 'increased' : 'decreased'} to ${next}`);

            // Either button disables at its bound, which would silently drop focus.
            if (next === 0) row.querySelector('[data-add]')?.focus();
            else if (button.disabled) row.querySelector(`[data-step="${next > previous ? -1 : 1}"]`)?.focus();
          });
        });
      }

      // Screen-reader live region. Clear first so repeating the same message is
      // re-announced.
      announce(message) {
        if (!this.liveRegion) return;

        this.liveRegion.textContent = '';
        clearTimeout(this.liveTimer);
        this.liveTimer = setTimeout(() => (this.liveRegion.textContent = message), 50);
      }

      // ---------- ship-by countdown ----------
      // The visual ticker updates every second but is aria-hidden; screen readers get
      // a parallel sentence with minute granularity, and only its time text node is
      // swapped (once a minute), so the virtual cursor has stable text to land on
      // instead of a sentence rebuilt every second.

      startCountdown() {
        if (!this.q('[data-ship-by]')) return;

        this.shipCountdown();
        const timer = setInterval(() => this.shipCountdown(), 1000);
        this.registerCleanup(() => clearInterval(timer));
      }

      shipCountdown() {
        const element = this.q('[data-ship-by]');
        if (!element) return;

        let local;
        try {
          local = new Date(new Date().toLocaleString('en-US', { timeZone: this.config.timezone || 'America/Chicago' }));
        } catch (error) {
          local = new Date();
        }

        const cutoff = new Date(local);
        cutoff.setHours(this.number(this.config.cutoffHour, 14), 0, 0, 0);
        const diff = cutoff - local;

        if (diff <= 0) {
          element.textContent = this.config.shipTomorrowText || '';
          this.lastScreenReaderTime = null;
          return;
        }

        const pad = (value) => (value < 10 ? '0' : '') + value;
        const hours = Math.floor(diff / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        const template = this.config.shipTodayText || 'Order in {time} and your first box ships today';

        let visual = element.querySelector('[data-ship-time]');
        let spoken = element.querySelector('[data-ship-time-sr]');

        if (!visual) {
          // Build the sentence once; afterwards only the time slots change.
          element.innerHTML =
            `<span aria-hidden="true">${template.replace('{time}', '<b data-ship-time></b>')}</span>` +
            `<span class="sr-only">${template.replace('{time}', '<span data-ship-time-sr></span>')}</span>`;
          visual = element.querySelector('[data-ship-time]');
          spoken = element.querySelector('[data-ship-time-sr]');
          this.lastScreenReaderTime = null;
        }

        visual.textContent = `${hours}h ${pad(minutes)}m ${pad(seconds)}s`;

        let spokenTime;
        if (hours > 0) {
          spokenTime = `${hours} ${hours === 1 ? 'hour' : 'hours'} and ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
        } else if (minutes > 0) {
          spokenTime = `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
        } else {
          spokenTime = 'less than a minute';
        }

        if (spoken && spokenTime !== this.lastScreenReaderTime) {
          spoken.textContent = spokenTime;
          this.lastScreenReaderTime = spokenTime;
        }
      }

      // ---------- add to cart ----------

      // The selling plan for a flavor under a given plan block, '' when none matched.
      planIdFor(flavor, plan) {
        return (flavor.sellingPlans || {})[plan.key] || '';
      }

      // A subscription plan with no selling plan resolved would post as a plain
      // one-time line — charging subscription pricing for a non-subscription.
      missingPlanFlavors() {
        const plan = this.currentPlan();
        if (plan.kind !== 'subscription') return [];
        return this.sellable.filter((flavor) => this.state.qty[flavor.key] > 0 && !this.planIdFor(flavor, plan));
      }

      selectedItems() {
        const plan = this.currentPlan();
        const isSubscription = plan.kind === 'subscription';

        return this.sellable
          .filter((flavor) => this.state.qty[flavor.key] > 0)
          .map((flavor) => {
            const item = { id: Number(flavor.variant), quantity: this.state.qty[flavor.key] };
            if (isSubscription) item.selling_plan = Number(this.planIdFor(flavor, plan));
            return item;
          });
      }

      handleErrorMessage(message = false) {
        const element = this.q('[data-error]');
        if (!element) return;

        element.toggleAttribute('hidden', !message);
        element.textContent = message || '';
      }

      trackAddToCart(items) {
        if (typeof window.fbq !== 'function') return;

        try {
          window.fbq('track', 'AddToCart', {
            content_type: 'product',
            content_ids: items.map((item) => String(item.id)),
            contents: items.map((item) => ({ id: String(item.id), quantity: item.quantity })),
            content_name: 'Product Bundle',
            value: this.charge(this.totalPacks()),
            currency: window.Shopify?.currency?.active || 'USD',
          });
        } catch (error) {
          /* a pixel failure must never block the cart */
        }
      }

      onSubmit(event) {
        if (this.cta.disabled || this.cta.hasAttribute('aria-busy')) return;

        const items = this.selectedItems();
        if (!items.length) return;

        const missing = this.missingPlanFlavors();
        if (missing.length) {
          this.handleErrorMessage(
            `No ${this.currentPlan().name} subscription is available for ${missing.map((flavor) => flavor.name).join(', ')}. Please choose another plan.`
          );
          return;
        }
        this.handleErrorMessage();

        if (document.body.classList.contains('template-cart') || theme.settings.cartType === 'page') {
          theme.utils.postLink2(theme.routes.cart_add_url, { parameters: { items } });
          return;
        }

        event.preventDefault();

        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

        const body = JSON.stringify({ items, sections: sectionsToBundle, sections_url: window.location.pathname });

        this.cta.setAttribute('aria-busy', 'true');
        this.cta.setAttribute('aria-disabled', 'true');

        fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig('javascript'), body })
          .then((response) => response.json())
          .then(async (parsedState) => {
            if (parsedState.status) {
              this.handleErrorMessage(parsedState.description);
              document.dispatchEvent(new CustomEvent('ajaxProduct:error', { detail: { errorMessage: parsedState.description } }));
              return;
            }

            this.trackAddToCart(items);

            const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
            cartJson.sections = parsedState.sections;

            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-bundle-hero', cart: cartJson });
            document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsedState } }));

            document.querySelector('cart-drawer')?.show(this.cta);
          })
          .catch((error) => {
            console.error(error);
            this.handleErrorMessage(theme.cartStrings?.error || 'Sorry, we could not add that to your cart.');
          })
          .finally(() => {
            this.cta.removeAttribute('aria-busy');
            this.cta.setAttribute('aria-disabled', this.totalPacks() > 0 ? 'false' : 'true');
          });
      }
    }
  );
}

// Ingredient tabs

if (!customElements.get('ingredient-tabs')) {
  customElements.define(
    'ingredient-tabs',
    class IngredientTabs extends BaseElement {
      connectedCallback() {
        super.connectedCallback();

        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[data-tab-panel]'));
        this.images = Array.from(this.querySelectorAll('[data-tab-image]'));
        if (this.tabs.length < 2) return;

        this.tabs.forEach((tab, index) => {
          this.on(tab, 'click', () => this.select(index));
          this.on(tab, 'keydown', (event) => this.onKeydown(event, index));
        });

        if (Shopify.designMode) {
          this.on(this, 'shopify:block:select', (event) => {
            const index = this.tabs.findIndex((tab) => tab === event.target || tab.contains(event.target));
            if (index > -1) this.select(index, false);
          });
        }
      }

      select(index, focus = true) {
        this.tabs.forEach((tab, i) => {
          tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
          tab.tabIndex = i === index ? 0 : -1;
        });
        this.panels.forEach((panel, i) => (panel.hidden = i !== index));
        this.images.forEach((image, i) => (image.hidden = i !== index));
        if (focus) this.tabs[index].focus();
      }

      onKeydown(event, index) {
        const last = this.tabs.length - 1;
        let next = null;

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = index === last ? 0 : index + 1;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = index === 0 ? last : index - 1;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = last;

        if (next === null) return;
        event.preventDefault();
        this.select(next);
      }
    }
  );
}

// Gallery hero — stacked images that spin through a five-slot deck once, then rest

if (!customElements.get('gallery-hero')) {
  customElements.define(
    'gallery-hero',
    class GalleryHero extends BaseElement {
      static SLOTS = 5;

      connectedCallback() {
        super.connectedCallback();

        this.items = Array.from(this.querySelectorAll('[data-stack-item]'));
        // Start with the first image at the front of the deck; a full pass ends there too.
        const count = this.items.length;
        const lead = Math.floor((GalleryHero.SLOTS - Math.min(count, GalleryHero.SLOTS)) / 2);
        this.offset = count ? (count - 2 + lead + count) % count : 0;
        this.render();

        const trigger = this.querySelector('[data-lightbox]');
        if (trigger) this.on(trigger, 'click', () => this.openLightbox());
        this.registerCleanup(() => this._photoswipe?.destroy?.());

        if (!this.hasAttribute('data-autoplay') || this.items.length < 2 || theme.config.motionReduced) return;

        this.observer = new IntersectionObserver(([entry]) => {
          if (!entry.isIntersecting) return;
          this.observer.disconnect();
          this.spin();
        }, { threshold: 0.25 });
        this.observer.observe(this);
        this.registerCleanup(() => {
          clearTimeout(this.timer);
          this.observer.disconnect();
        });
      }

      // A fixed cadence over enough steps to land back on the first image.
      spin() {
        const delay = parseInt(this.dataset.interval, 10) || 350;
        const total = this.items.length * (parseInt(this.dataset.spins, 10) || 1);
        let index = 0;

        this.style.setProperty('--gh-speed', `${Math.round(delay * 0.85)}ms`);

        const tick = () => {
          if (index >= total) return;
          this.step();
          index += 1;
          this.timer = setTimeout(tick, delay);
        };

        this.timer = setTimeout(tick, delay);
      }

      step() {
        this.offset = (this.offset + 1) % this.items.length;
        this.render();
      }

      // Same lightbox the product gallery opens, fed the stack's images.
      get photoswipe() {
        if (this._photoswipe) return this._photoswipe;

        const lightbox = new PhotoSwipeLightbox({
          arrowPrevSVG: '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="M17.5 7.5L10 15L17.5 22.5"/></svg>',
          arrowNextSVG: '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="M17.5 7.5L10 15L17.5 22.5"/></svg>',
          closeSVG: '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="m7.5 22.5 15-15m-15 0 15 15"/></svg>',
          bgOpacity: 1,
          pswpModule: () => import(theme.settings.pswpModule),
          arrowPrevTitle: theme.strings.previous,
          arrowNextTitle: theme.strings.next,
          closeTitle: theme.strings.close,
          allowPanToNext: false,
          allowMouseDrag: true,
          wheelToZoom: false,
          returnFocus: true,
          zoom: false,
        });
        lightbox.addFilter('thumbEl', (_thumbEl, data) => data.thumbnailElement);
        lightbox.init();

        return (this._photoswipe = lightbox);
      }

      openLightbox() {
        if (typeof PhotoSwipeLightbox === 'undefined') {
          console.warn('[gallery-hero] PhotoSwipeLightbox is not loaded');
          return;
        }

        const dataSource = this.items.map((item) => {
          const image = item.querySelector('img');
          if (!image) return { html: item.innerHTML, width: 1200, height: 790 };
          return {
            thumbnailElement: image,
            src: image.src,
            srcset: image.srcset,
            msrc: image.currentSrc || image.src,
            width: parseInt(image.getAttribute('width'), 10) || 1200,
            height: parseInt(image.getAttribute('height'), 10) || 790,
            alt: image.alt,
            thumbCropped: true,
          };
        });
        if (!dataSource.length) return;

        const front = this.items.findIndex((item) => item.dataset.slot === '2');
        this.photoswipe.loadAndOpen(Math.max(0, front), dataSource);
      }

      // Slot 0 is the back of the deck at the top, slot 2 the front, slot 4 the
      // back at the bottom. Anything past the last slot waits above the deck so
      // it re-enters from the top.
      render() {
        const count = this.items.length;
        const visible = Math.min(count, GalleryHero.SLOTS);
        const lead = Math.floor((GalleryHero.SLOTS - visible) / 2);

        this.items.forEach((item, index) => {
          const slot = (index - this.offset + count) % count;
          const previous = item.dataset.slot;
          const next = slot < visible ? String(slot + lead) : slot === visible ? 'exit' : 'wait';

          if (next === '0' && previous !== undefined && previous !== 'wait') {
            item.dataset.slot = 'wait';
            void item.offsetWidth;
          }
          item.dataset.slot = next;
        });
      }
    }
  );
}

// Rigby header

class RigbyDropdown extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    if (!this.details) return;

    this.abortController = new AbortController();
    const options = { signal: this.abortController.signal };
    const hoverable = window.matchMedia('(hover: hover)');

    this.addEventListener('mouseenter', () => hoverable.matches && this.open(), options);
    this.addEventListener('mouseleave', () => hoverable.matches && this.close(150), options);
    this.details.querySelector('summary').addEventListener('click', (event) => {
      if (!hoverable.matches) return;
      event.preventDefault();
      this.open();
    }, options);
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.close();
    }, options);
    document.addEventListener('keyup', (event) => {
      if (event.code === 'Escape' && this.details.open) {
        this.close();
        this.details.querySelector('summary').focus();
      }
    }, options);
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.close();
    }, options);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    clearTimeout(this.timer);
  }

  open() {
    clearTimeout(this.timer);
    this.details.open = true;
  }

  close(delay = 0) {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => (this.details.open = false), delay);
  }
}

if (!customElements.get('rigby-dropdown')) {
  customElements.define('rigby-dropdown', RigbyDropdown);
}

document.addEventListener('click', (event) => {
  const cartLink = event.target.closest('.rigby-drawer__cart');
  if (cartLink) cartLink.closest('drawer-element')?.hide();
});

// Rigby cart: delivery frequency + upsell

const rigbyCartRequest = (url, payload) => {
  const sections = [];
  document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));

  return fetch(url, { ...theme.utils.fetchConfig(), body: JSON.stringify({ ...payload, sections }) })
    .then((response) => response.json())
    .then((cart) => {
      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'rigby-cart', cart });
      return cart;
    });
};

document.addEventListener('change', (event) => {
  const select = event.target.closest?.('[data-rigby-selling-plan]');
  if (!select) return;
  event.stopPropagation();

  const value = select.value;
  select.disabled = true;
  select.closest('.horizontal-product')?.classList.add('opacity-50');

  rigbyCartRequest(theme.routes.cart_change_url, {
    id: select.dataset.lineKey,
    quantity: parseInt(select.dataset.lineQuantity, 10),
    selling_plan: value === 'ONETIME' ? null : Number(value)
  }).catch((error) => console.error(error));
}, true);

document.addEventListener('click', (event) => {
  const button = event.target.closest?.('[data-rigby-upsell-add]');
  if (!button || button.disabled) return;

  const item = {
    id: Number(button.dataset.rigbyUpsellAdd),
    quantity: parseInt(button.dataset.rigbyUpsellQty, 10) || 1
  };
  if (button.dataset.rigbyUpsellPlan) item.selling_plan = Number(button.dataset.rigbyUpsellPlan);

  button.disabled = true;
  rigbyCartRequest(`${theme.routes.cart_add_url}.js`, { items: [item] })
    .catch((error) => console.error(error))
    .finally(() => {
      if (button.isConnected) button.disabled = false;
    });
});
